import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type {
  LueftungBewertung, LueftungLuftmengenmodell, LueftungLuftmengenvariante, LueftungLuftmengenzeile,
  LueftungPruefantwort, LueftungSeitenzeile, LueftungVariantenbewertung, LueftungVariantenzaehlung,
} from '../../api';
import type { Rahmen } from '../Projektrahmen';
import { PageHeader } from '../../rahmen/PageHeader';
import { Raumstandhinweis } from '../Raumstandhinweis';
import {
  ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, FilterBar, KpiCard, KpiReihe, LoadingState,
  StatusBadge, Vorbehalte, formatiereZahl, spalteFuer, useFilterInUrl, type FilterDefinition, type Spalte,
} from '../../muster';
import {
  AnnahmenAbschnitt, NormAbschnitt, Rechenstand, VARIANTEN_ANKER, VARIANTEN_TITEL, bewertungStatus, co2Stufentext,
  optionenAus, seitenVorbehalte, sollarttext, variantenStatus, variantentext,
} from './pruefung';

type Antwort = LueftungPruefantwort<LueftungLuftmengenmodell, LueftungLuftmengenzeile>;
/** Die Seitenzeile mit ihrer Stelle im Lauf - der stabile Schlüssel der Tabelle. */
type Zeile = LueftungSeitenzeile<LueftungLuftmengenzeile> & { nr: number };

const BEWERTUNGEN: LueftungBewertung[] = [
  'unter_soll', 'unplausibel', 'ueber_soll', 'fehlt', 'kein_soll', 'nur_untergrenze', 'im_rahmen',
];

/** TV5c: die Sollarten in der Reihenfolge des Modells (TV4). */
const SOLLARTEN = ['auslegung', 'untergrenze', 'extern', 'sanitaer', 'bereich'];

/** TV5c: eine Variante mit ihrer Stelle im Lauf - der stabile Schlüssel der Tabelle. */
type Variantenzeile = LueftungLuftmengenvariante & { nr: number };

const VARIANTENERGEBNISSE: LueftungVariantenbewertung[] = [
  'unter_soll', 'unplausibel', 'ueber_soll', 'fehlt', 'im_rahmen', 'nicht_rechenbar',
];

/** CO₂ in ppm: eigene Spalte ohne Größe im Einheitenkatalog (TX), ganzzahlig wie im PDF. */
const CO2_SPALTE = { art: 'zahl' as const, einheit: 'ppm', nachkomma: 0 };

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LUFTMENGEN JE RAUM (TV5, Lauf luftmengen-pruefen)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bis TV5 hing der Soll-Ist-Vergleich als Abschnitt in `Druckverlust.tsx` und
 * war auf 200 von 978 Zeilen gekappt (Befund TV §5 Nr. 2). Hier stehen ALLE
 * Zeilen, mit Bewertung nach S1.5 und den Vorbehalten, die die Bewertung heute
 * noch hat: Rückfall-Soll, Istwerte aus Raumbuch-Summen mit Kanalzubehör, der
 * Maßstab für Küche, Technik und WC. Seit TV4 (v0.52.13) kennt die Bewertung die
 * Stufe „nur Untergrenze geprüft“ - die Seite rechnet nichts um, sie zeigt. Die
 * Vorbehalte kommen seit TV5b aus dem Datenmodell, wortgleich mit dem PDF LUE-LMG.
 */
export function LueftungLuftmengen() {
  const { api, projekt } = useOutletContext<Rahmen>();
  const [daten, setDaten] = useState<Antwort | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laedt, setLaedt] = useState(true);

  const laden = useCallback(() => {
    setLaedt(true);
    setFehler(null);
    api.lueftungLuftmengenliste(projekt.id)
      .then(setDaten)
      .catch(setFehler)
      .finally(() => setLaedt(false));
  }, [api, projekt.id]);

  useEffect(() => { laden(); }, [laden]);

  const zeilen = useMemo<Zeile[]>(() => (daten?.zeilen ?? []).map((z, nr) => ({ ...z, nr })), [daten]);

  const filter = useMemo<FilterDefinition[]>(() => [
    {
      schluessel: 'bewertung', beschriftung: 'Bewertung', art: 'mehrfachauswahl',
      optionen: BEWERTUNGEN.map((b) => ({ wert: b, beschriftung: bewertungStatus(b).text })),
    },
    {
      schluessel: 'ebene', beschriftung: 'Ebene', art: 'auswahl', platzhalter: 'alle',
      optionen: optionenAus(zeilen.map((z) => z.zeile.ebene)),
    },
    {
      schluessel: 'nutzung', beschriftung: 'Nutzungsart', art: 'auswahl', platzhalter: 'alle',
      optionen: optionenAus(zeilen.map((z) => z.zeile.nutzungsart)),
    },
    { schluessel: 'rueckfall', beschriftung: 'nur Soll aus Rückfall', art: 'haken' },
    {
      schluessel: 'sollart', beschriftung: 'Sollart', art: 'mehrfachauswahl',
      optionen: SOLLARTEN.map((a) => ({ wert: a, beschriftung: sollarttext(a) })),
    },
  ], [zeilen]);
  const [werte, setWerte] = useFilterInUrl(filter);

  const gefiltert = useMemo(() => {
    const bewertung = (werte.bewertung as string[]) ?? [];
    const sollart = (werte.sollart as string[]) ?? [];
    return zeilen.filter((z) => (bewertung.length === 0 || bewertung.includes(z.zeile.bewertung))
      && (!werte.ebene || z.zeile.ebene === werte.ebene)
      && (!werte.nutzung || z.zeile.nutzungsart === werte.nutzung)
      && (!werte.rueckfall || z.zeile.sollRueckfall)
      && (sollart.length === 0 || sollart.includes(z.zeile.sollArt ?? '')));
  }, [zeilen, werte]);

  // ═══ TV5c: Varianten - eigener Filter mit Vorsatz „v_“ in der Adresse ═══
  const varianten = useMemo<Variantenzeile[]>(
    () => (daten?.modell?.varianten ?? []).map((v, nr) => ({ ...v, nr })), [daten]);
  const variantenFilter = useMemo<FilterDefinition[]>(() => [
    {
      schluessel: 'variante', beschriftung: 'Variante', art: 'mehrfachauswahl',
      optionen: (daten?.modell?.jeVariante ?? []).map((v) => ({ wert: v.variante, beschriftung: variantentext(v.variante) })),
    },
    {
      schluessel: 'ergebnis', beschriftung: 'Ergebnis der Variante', art: 'mehrfachauswahl',
      optionen: VARIANTENERGEBNISSE.map((b) => ({ wert: b, beschriftung: variantenStatus(b).text })),
    },
  ], [daten]);
  const [vWerte, setVWerte] = useFilterInUrl(variantenFilter, 'v_');
  const variantenGefiltert = useMemo(() => {
    const art = (vWerte.variante as string[]) ?? [];
    const ergebnis = (vWerte.ergebnis as string[]) ?? [];
    return varianten.filter((v) => (art.length === 0 || art.includes(v.variante))
      && (ergebnis.length === 0 || ergebnis.includes(v.bewertung)));
  }, [varianten, vWerte]);

  const variantenSpalten = useMemo<Spalte<Variantenzeile>[]>(() => [
    { schluessel: 'variante', titel: 'Variante', immerSichtbar: true, wert: (v) => variantentext(v.variante) },
    { schluessel: 'fachfrage', titel: 'Fachfrage', wert: (v) => v.fachfrage ?? '' },
    { schluessel: 'raum', titel: 'Raum', wert: (v) => v.raumnummer ?? (v.bereich ? `Bereich ${v.bereich}` : '') },
    { schluessel: 'name', titel: 'Raumname', wert: (v) => v.raumname ?? '' },
    { schluessel: 'personen', titel: 'Personen', art: 'zahl', nachkomma: 0, wert: (v) => v.personen },
    { schluessel: 'soll', titel: 'Soll', ...spalteFuer('volumenstrom_luft'), wert: (v) => v.sollM3h },
    { schluessel: 'ist', titel: 'Ist', ...spalteFuer('volumenstrom_luft'), wert: (v) => v.istM3h },
    { schluessel: 'abweichung', titel: 'Abweichung', ...spalteFuer('anteil'), wert: (v) => v.abweichungProzent },
    {
      schluessel: 'ergebnis', titel: 'Ergebnis der Variante', art: 'status', wert: (v) => variantenStatus(v.bewertung).text,
      inhalt: (v) => <StatusBadge {...variantenStatus(v.bewertung)} titel="nicht entschieden" klein />,
    },
    { schluessel: 'co2', titel: 'CO₂', ...CO2_SPALTE, wert: (v) => v.co2Ppm },
    { schluessel: 'annahmen', titel: 'Rechnung und Annahmen', wert: (v) => v.annahmen ?? '' },
  ], []);

  const zaehlSpalten = useMemo<Spalte<LueftungVariantenzaehlung>[]>(() => [
    { schluessel: 'variante', titel: 'Variante', immerSichtbar: true, wert: (z) => variantentext(z.variante) },
    { schluessel: 'fachfrage', titel: 'Fachfrage', wert: (z) => z.fachfrage ?? '' },
    { schluessel: 'gesamt', titel: 'gesamt', art: 'zahl', nachkomma: 0, wert: (z) => z.gesamt },
    { schluessel: 'unter', titel: 'unter Soll', art: 'zahl', nachkomma: 0, wert: (z) => z.unterSoll },
    { schluessel: 'rahmen', titel: 'im Rahmen', art: 'zahl', nachkomma: 0, wert: (z) => z.imRahmen },
    { schluessel: 'ueber', titel: 'über Soll', art: 'zahl', nachkomma: 0, wert: (z) => z.ueberSoll },
    { schluessel: 'fehlt', titel: 'Ist fehlt', art: 'zahl', nachkomma: 0, wert: (z) => z.fehlt },
    { schluessel: 'unplausibel', titel: 'unplausibel', art: 'zahl', nachkomma: 0, wert: (z) => z.unplausibel },
    { schluessel: 'nichtRechenbar', titel: 'nicht rechenbar', art: 'zahl', nachkomma: 0, wert: (z) => z.nichtRechenbar },
  ], []);

  const sollartSpalten = useMemo<Spalte<{ sollArt: string; anzahl: number }>[]>(() => [
    { schluessel: 'sollart', titel: 'Sollart', immerSichtbar: true, wert: (a) => sollarttext(a.sollArt) },
    { schluessel: 'anzahl', titel: 'Räume und Bereiche', art: 'zahl', nachkomma: 0, wert: (a) => a.anzahl },
  ], []);

  const co2Spalten = useMemo<Spalte<{ stufe: string; anzahl: number }>[]>(() => [
    { schluessel: 'stufe', titel: 'CO₂ nach ASR A3.6', immerSichtbar: true, wert: (a) => a.stufe },
    { schluessel: 'anzahl', titel: 'Räume', art: 'zahl', nachkomma: 0, wert: (a) => a.anzahl },
  ], []);

  const spalten = useMemo<Spalte<Zeile>[]>(() => [
    { schluessel: 'ebene', titel: 'Ebene', wert: (z) => z.zeile.ebene ?? '' },
    {
      schluessel: 'raum', titel: 'Raum', immerSichtbar: true,
      wert: (z) => z.zeile.raumnummer ?? z.zeile.bereich ?? '',
      inhalt: (z) => (z.zeile.raumnummer
        ? z.zeile.raumnummer
        : <span>Bereich {z.zeile.bereich}{z.zeile.raeumeAnzahl ? ` (${formatiereZahl(z.zeile.raeumeAnzahl, 0)} Räume)` : ''}</span>),
    },
    { schluessel: 'name', titel: 'Raumname', wert: (z) => z.zeile.raumname ?? '' },
    { schluessel: 'nutzung', titel: 'Nutzungsart', wert: (z) => z.zeile.nutzungsart ?? '' },
    { schluessel: 'personen', titel: 'Personen', art: 'zahl', nachkomma: 0, wert: (z) => z.zeile.personen },
    { schluessel: 'flaeche', titel: 'Fläche', ...spalteFuer('flaeche'), wert: (z) => z.zeile.flaecheM2 },
    { schluessel: 'soll', titel: 'Soll', ...spalteFuer('volumenstrom_luft'), wert: (z) => z.zeile.sollM3h },
    {
      schluessel: 'sollHerkunft', titel: 'Herkunft Soll', art: 'status', wert: (z) => z.zeile.sollRueckfall,
      inhalt: (z) => (z.zeile.sollRueckfall
        ? <StatusBadge zustand="warnung" text="Rückfall" titel="Soll aus Fläche oder Luftwechsel, nicht aus Personen - nur Untergrenze" klein />
        : <StatusBadge zustand="neutral" text="Nutzung" klein />),
    },
    { schluessel: 'ist', titel: 'Ist', ...spalteFuer('volumenstrom_luft'), wert: (z) => z.zeile.istM3h },
    { schluessel: 'istQuelle', titel: 'Quelle Ist', wert: (z) => z.zeile.istQuelle ?? '' },
    { schluessel: 'abweichung', titel: 'Abweichung', ...spalteFuer('anteil'), wert: (z) => z.zeile.abweichungProzent },
    {
      schluessel: 'bewertung', titel: 'Bewertung', art: 'status', wert: (z) => bewertungStatus(z.zeile.bewertung).text,
      inhalt: (z) => <StatusBadge {...bewertungStatus(z.zeile.bewertung)} klein />,
    },
    { schluessel: 'sollart', titel: 'Sollart', wert: (z) => (z.zeile.sollArt ? sollarttext(z.zeile.sollArt) : '') },
    { schluessel: 'co2', titel: 'CO₂', ...CO2_SPALTE, wert: (z) => z.zeile.co2Ppm },
    { schluessel: 'co2Stufe', titel: 'CO₂-Stufe', wert: (z) => co2Stufentext(z.zeile.co2Stufe) },
    { schluessel: 'herkunftText', titel: 'Soll im Einzelnen', wert: (z) => z.zeile.sollHerkunft ?? '', versteckt: true },
    { schluessel: 'zuluft', titel: 'Ist Zuluft', ...spalteFuer('volumenstrom_luft'), wert: (z) => z.zeile.istZuluftM3h, versteckt: true },
    { schluessel: 'abluft', titel: 'Ist Abluft', ...spalteFuer('volumenstrom_luft'), wert: (z) => z.zeile.istAbluftM3h, versteckt: true },
    { schluessel: 'marken', titel: 'Kennzeichnungen', wert: (z) => z.zeile.kennzeichnungen ?? '', versteckt: true },
    { schluessel: 'hinweis', titel: 'Hinweis', wert: (z) => z.zeile.hinweis ?? '' },
  ], []);

  const m = daten?.modell ?? null;
  const ohneLauf = !!daten && !daten.lauf;
  const gesperrt = !daten?.lauf ? { grund: 'Noch kein Lauf luftmengen-pruefen.' } : undefined;
  const anzahl = (b: LueftungBewertung) => m?.jeBewertung.find((x) => x.bewertung === b)?.anzahl ?? 0;

  return (
    <>
      <PageHeader
        titel="Luftmengen"
        beschreibung="Soll und Ist der Luftmenge je Raum - alle Räume, mit Bewertung und den Vorbehalten der Bewertung. Je Anlage zeigt sie die Anlagenliste."
        meta={<Rechenstand lauf={daten?.lauf} datenhash={daten?.datenhash} />}
        aktionen={(
          <ExportLeiste ziele={[
            { art: 'pdf', beschriftung: 'PDF LUE-LMG', gesperrt, laden: () => api.lueftungPruefberichtPdf(projekt.id, 'luftmengen') },
            { art: 'excel', beschriftung: 'Excel', gesperrt, laden: () => api.lueftungPruefListen(projekt.id, 'luftmengen') },
          ]}
          />
        )}
      />

      <Raumstandhinweis api={api} projektId={projekt.id} rechnungen={['luftmengen']} />

      {laedt && !daten && <LoadingState text="Luftmengen werden abgerufen …" zeilen={10} />}
      {fehler !== null && (
        <ErrorState
          meldung="Die Luftmengen konnten nicht abgerufen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      )}

      {ohneLauf && (
        <EmptyState
          titel="Noch keine Luftmengen gerechnet."
          erklaerung="Die Luftmengen je Raum entstehen mit dem Serverwerkzeug luftmengen-pruefen aus Raumbuch, Nutzung und Luftmengenansätzen."
        />
      )}

      {daten?.lauf && m && (
        <>
          <Vorbehalte eintraege={seitenVorbehalte([m])} />

          <KpiReihe>
            {/* TV5b: höchstens 6 Karten (S1.2 Nr. 3) - seit TV4 die Karte „nur Untergrenze geprüft“; die Zahl
                der Rückfall-Sollwerte steht deshalb als Vergleich an der Gesamtzahl. */}
            <KpiCard
              titel="Räume und Bereiche" wert={m.raeume}
              vergleich={`${formatiereZahl(m.sollRueckfall, 0)} mit Soll aus Rückfall`}
            />
            <KpiCard
              titel="unter Soll" wert={anzahl('unter_soll')}
              zustand={anzahl('unter_soll') > 0 ? 'fehler' : 'gut'} zustandText={anzahl('unter_soll') > 0 ? 'unterversorgt' : 'keine'}
            />
            <KpiCard titel="über Soll" wert={anzahl('ueber_soll')} vergleich="vorläufig, siehe Vorbehalte" />
            <KpiCard titel="Ist fehlt" wert={anzahl('fehlt')} />
            <KpiCard titel="im Rahmen" wert={anzahl('im_rahmen')} />
            <KpiCard titel="nur Untergrenze geprüft" wert={anzahl('nur_untergrenze')} vergleich="Auslegung offen" />
          </KpiReihe>

          <ContentSection
            titel="Luftmengen je Raum"
            beschreibung="Alle Zeilen des Laufs, ohne Kappung. „Rückfall“ heißt: Das Soll stammt aus Fläche oder Luftwechsel, nicht aus Personen."
            werkzeuge={<FilterBar filter={filter} werte={werte} beiAenderung={setWerte} inLeiste={4} />}
          >
            <DataTable<Zeile>
              name="Luftmengen je Raum"
              spalten={spalten}
              zeilen={gefiltert}
              schluessel={(z) => String(z.nr)}
              ausfuhr
              ausfuhrDateiname="luftmengen.csv"
              suchePlatzhalter="Raum, Name, Nutzung …"
              leer={(
                <EmptyState
                  klein
                  titel="Kein Raum passt zu den Filtern."
                  erklaerung="Der Lauf hat Zeilen; die gesetzten Filter schließen alle aus."
                />
              )}
            />
          </ContentSection>

          <ContentSection
            titel="Sollart und CO₂"
            beschreibung="Was die Sollwerte sind (seit TV4) und die CO₂-Abschätzung nach ASR A3.6 für Räume mit belastbarer Personenzahl - gezählt aus dem Lauf, mit unbestätigten Annahmen (siehe „Annahmen und Vorgaben“)."
          >
            <DataTable
              name="Räume je Sollart"
              spalten={sollartSpalten}
              zeilen={m.jeSollArt}
              schluessel={(a) => a.sollArt || 'ohne'}
              leer={<span>Der Lauf nennt keine Sollart.</span>}
            />
            <DataTable
              name="CO₂ je Stufe"
              spalten={co2Spalten}
              zeilen={[
                { stufe: co2Stufentext('bis_1000'), anzahl: m.co2.bis1000 },
                { stufe: co2Stufentext('bis_2000'), anzahl: m.co2.bis2000 },
                { stufe: co2Stufentext('ueber_2000'), anzahl: m.co2.ueber2000 },
                { stufe: 'ohne Abschätzung (keine belastbare Personenzahl oder kein Ist)', anzahl: m.raeume - m.co2.mitWert },
              ]}
              schluessel={(a) => a.stufe}
              leer={<span>Keine CO₂-Abschätzung.</span>}
            />
          </ContentSection>

          <ContentSection
            id={VARIANTEN_ANKER}
            titel={VARIANTEN_TITEL}
            beschreibung="Je offener Fachfrage rechnet der Lauf, was mit der anderen Antwort herauskäme. Keine Variante ist entschieden: Sie ändern weder Soll noch Bewertung eines Raums und erzeugen keinen Befund - deshalb ohne Farbe."
            werkzeuge={varianten.length > 0
              ? <FilterBar filter={variantenFilter} werte={vWerte} beiAenderung={setVWerte} inLeiste={2} />
              : undefined}
          >
            <DataTable
              name="Varianten je Fachfrage"
              spalten={zaehlSpalten}
              zeilen={m.jeVariante}
              schluessel={(z) => z.variante}
              leer={<span>Dieser Lauf hat keine Varianten (Lauf vor TV4 oder keine offene Fachfrage betroffen).</span>}
            />
            <DataTable<Variantenzeile>
              name="Alle Varianten"
              spalten={variantenSpalten}
              zeilen={variantenGefiltert}
              schluessel={(v) => String(v.nr)}
              ausfuhr={variantenGefiltert.length > 20}
              ausfuhrDateiname="luftmengen-varianten.csv"
              suchePlatzhalter="Raum, Name, Variante …"
              leer={varianten.length === 0
                ? <span>Dieser Lauf hat keine Varianten.</span>
                : (
                  <EmptyState
                    klein
                    titel="Keine Variante passt zu den Filtern."
                    erklaerung="Der Lauf hat Varianten; die gesetzten Filter schließen alle aus."
                  />
                )}
            />
          </ContentSection>

          <AnnahmenAbschnitt annahmen={m.annahmen} name="Luftmengen" />
          <NormAbschnitt normen={m.normen} name="Luftmengen" />
        </>
      )}
    </>
  );
}
